import type {
  PatientBaseProfile,
  PatientMeasurementField,
  PatientMeasurementMetricType,
  PatientMeasurementRecord,
} from '../types';
import { arePatientMeasurementFieldsComplete } from './measurementValidation';

export const isFullBodyMeasurementRecord = (
  record: PatientMeasurementRecord
): boolean => arePatientMeasurementFieldsComplete(record.fields);

interface BaseFieldDefinition {
  label: string;
  unit: string;
  metricType: Exclude<PatientMeasurementMetricType, null>;
}

export const BASE_MEASUREMENT_FIELD_DEFINITIONS: BaseFieldDefinition[] = [
  { label: 'Peso', unit: 'kg', metricType: 'weight_kg' },
  { label: 'Spalle', unit: 'cm', metricType: 'shoulder_cm' },
  { label: 'Fianchi', unit: 'cm', metricType: 'hips_cm' },
  { label: 'Vita', unit: 'cm', metricType: 'waist_cm' },
  { label: 'Petto', unit: 'cm', metricType: 'chest_cm' },
  { label: 'Collo', unit: 'cm', metricType: 'neck_cm' },
  { label: 'Coscia', unit: 'cm', metricType: 'thigh_cm' },
  { label: 'Polpacci', unit: 'cm', metricType: 'calf_cm' },
];

const createFieldId = (prefix: string, position: number) =>
  `${prefix}-${position}-${Math.random().toString(36).slice(2, 8)}`;

export const createBaseMeasurementFields = (): PatientMeasurementField[] =>
  BASE_MEASUREMENT_FIELD_DEFINITIONS.map((field, index) => ({
    id: createFieldId('base-field', index),
    label: field.label,
    value: '',
    unit: field.unit,
    metricType: field.metricType,
    position: index,
    scope: 'base',
  }));

export const createExtraMeasurementField = (
  position: number,
  values?: Partial<PatientMeasurementField>
): PatientMeasurementField => ({
  id: createFieldId('extra-field', position),
  label: values?.label?.trim() ?? '',
  value: '',
  unit: values?.unit?.trim() ?? '',
  metricType: values?.metricType ?? null,
  position,
  scope: 'extra',
});

const isDefaultBaseField = (field: PatientMeasurementField) =>
  BASE_MEASUREMENT_FIELD_DEFINITIONS.some(
    definition =>
      definition.metricType === field.metricType &&
      definition.label === field.label
  );

const normalizeFieldScope = (
  field: PatientMeasurementField
): PatientMeasurementField => ({
  ...field,
  scope: field.scope ?? (isDefaultBaseField(field) ? 'base' : 'extra'),
});

const mergeBaseFieldsWithHistory = (
  latestMeasurement: PatientMeasurementRecord | null,
  options?: { prefillValues?: boolean }
): PatientMeasurementField[] => {
  const prefillValues = options?.prefillValues === true;
  const latestFields = latestMeasurement?.fields.map(normalizeFieldScope) ?? [];
  const baseFields = createBaseMeasurementFields();

  const mappedBaseFields = BASE_MEASUREMENT_FIELD_DEFINITIONS.map(
    (definition, index) => {
      const previous = latestFields.find(
        field =>
          field.metricType === definition.metricType &&
          field.label === definition.label
      );

      return {
        ...(previous ?? baseFields[index]),
        id: previous?.id ?? baseFields[index]?.id,
        label: definition.label,
        unit: definition.unit,
        metricType: definition.metricType,
        value: prefillValues ? (previous?.value ?? '') : '',
        position: index,
        scope: 'base' as const,
      };
    }
  );

  const extraFields = latestFields
    .filter(
      field =>
        (field.scope ?? 'extra') === 'extra' && !isDefaultBaseField(field)
    )
    .map((field, index) => ({
      ...field,
      id:
        field.id ??
        createFieldId('extra-field', mappedBaseFields.length + index),
      value: prefillValues ? field.value : '',
      position: mappedBaseFields.length + index,
      scope: 'extra' as const,
    }));

  return [...mappedBaseFields, ...extraFields];
};

export const createMeasurementTemplate = (
  patientId: number,
  latestMeasurement: PatientMeasurementRecord | null,
  profile?: PatientBaseProfile | null
): PatientMeasurementRecord => ({
  patientId,
  notes: '',
  fields: mergeBaseFieldsWithHistory(latestMeasurement),
  biologicalSex: profile?.biologicalSex ?? null,
  heightCm: profile?.heightCm ?? null,
  dateOfBirth: profile?.dateOfBirth ?? null,
});

export const createMeasurementPrefillFromLatest = (
  patientId: number,
  latestMeasurement: PatientMeasurementRecord | null,
  profile?: PatientBaseProfile | null
): PatientMeasurementRecord => ({
  patientId,
  notes: latestMeasurement?.notes ?? '',
  fields: mergeBaseFieldsWithHistory(latestMeasurement, {
    prefillValues: true,
  }),
  biologicalSex: profile?.biologicalSex ?? null,
  heightCm: profile?.heightCm ?? null,
  dateOfBirth: profile?.dateOfBirth ?? '',
});

export const sanitizeMeasurementFields = (
  fields: PatientMeasurementField[]
): PatientMeasurementField[] =>
  fields
    .map((field, index) => ({
      ...field,
      label: field.label.trim(),
      value: field.value.trim(),
      unit: field.unit?.trim() || null,
      position: index,
      scope: field.scope ?? (isDefaultBaseField(field) ? 'base' : 'extra'),
    }))
    .filter(field => field.label !== '');
